import type { RoofType, StylePreset } from '@/model/schema';

/**
 * Constraint model: hard vs soft.
 *
 * A `Constraint<T>` carries a value AND whether it's currently locked by the
 * user. Locked constraints MUST hold in the output (hard constraint). Unlocked
 * constraints MAY be relaxed by the generator if honoring them prevents a
 * plausible result (soft preference).
 *
 * The distinction is critical (see design doc §"Constraints Model"): treating
 * everything hard collapses into failures; treating everything soft makes
 * users feel ignored.
 */
export interface Constraint<T> {
  value: T;
  locked: boolean;
}

export function hard<T>(value: T): Constraint<T> {
  return { value, locked: true };
}
export function soft<T>(value: T): Constraint<T> {
  return { value, locked: false };
}

/** Numeric range constraint [min, max]. Locked means the generator must stay
 * inside the range. Unlocked means the range is merely a preference. */
export interface RangeConstraint {
  min: number;
  max: number;
  locked: boolean;
}

export function range(min: number, max: number, locked = false): RangeConstraint {
  if (min > max) throw new Error(`range: min(${min}) > max(${max})`);
  return { min, max, locked };
}

export function clampToRange(value: number, r: RangeConstraint): number {
  return Math.min(r.max, Math.max(r.min, value));
}

/**
 * User-facing input to the generator. All fields are optional because the UI
 * lets users leave things "open".
 */
export interface GenerationConstraints {
  seed: string;
  /** Optional preset pin. If unlocked, the generator picks one per seed. */
  stylePreset?: Constraint<StylePreset>;
  /** Total stories (e.g. 1..3). */
  storyCount?: Constraint<number>;
  /** Lot dimensions in meters. */
  lotWidth?: RangeConstraint;
  lotDepth?: RangeConstraint;
  /** Roof type. */
  roofType?: Constraint<RoofType>;
  /** Presence toggles. */
  fence?: Constraint<boolean>;
  mailbox?: Constraint<boolean>;
  /** Counts. */
  bedrooms?: Constraint<number>;
  bathrooms?: Constraint<number>;
  /** Front-facade symmetry preference. */
  symmetricFront?: Constraint<boolean>;
  /** Include a below-grade basement. */
  basement?: Constraint<boolean>;
  /** Include an attached garage. */
  garage?: Constraint<boolean>;
}

/* --------------------------- resolved constraints --------------------- */

/**
 * After `resolveConstraints`, every field has a concrete value. Hard
 * constraints are frozen; soft constraints have been nudged to their
 * preferred value but may still be moved by the pipeline.
 */
export interface ResolvedConstraints {
  seed: string;
  stylePreset: { value: StylePreset; hard: boolean };
  storyCount: { value: number; hard: boolean };
  lotWidth: { min: number; max: number; hard: boolean };
  lotDepth: { min: number; max: number; hard: boolean };
  roofType: { value: RoofType; hard: boolean };
  fence: { value: boolean; hard: boolean };
  mailbox: { value: boolean; hard: boolean };
  bedrooms: { value: number; hard: boolean };
  bathrooms: { value: number; hard: boolean };
  symmetricFront: { value: boolean; hard: boolean };
  basement: { value: boolean; hard: boolean };
  garage: { value: boolean; hard: boolean };
}

export const DEFAULT_LOT_WIDTH: RangeConstraint = range(10, 22);
export const DEFAULT_LOT_DEPTH: RangeConstraint = range(12, 26);

export function resolveConstraints(input: GenerationConstraints): ResolvedConstraints {
  return {
    seed: input.seed,
    stylePreset: {
      value: input.stylePreset?.value ?? 'suburban',
      hard: input.stylePreset?.locked ?? false,
    },
    storyCount: {
      value: input.storyCount?.value ?? 2,
      hard: input.storyCount?.locked ?? false,
    },
    lotWidth: {
      min: input.lotWidth?.min ?? DEFAULT_LOT_WIDTH.min,
      max: input.lotWidth?.max ?? DEFAULT_LOT_WIDTH.max,
      hard: input.lotWidth?.locked ?? false,
    },
    lotDepth: {
      min: input.lotDepth?.min ?? DEFAULT_LOT_DEPTH.min,
      max: input.lotDepth?.max ?? DEFAULT_LOT_DEPTH.max,
      hard: input.lotDepth?.locked ?? false,
    },
    roofType: {
      value: input.roofType?.value ?? 'gable',
      hard: input.roofType?.locked ?? false,
    },
    fence: { value: input.fence?.value ?? false, hard: input.fence?.locked ?? false },
    mailbox: { value: input.mailbox?.value ?? true, hard: input.mailbox?.locked ?? false },
    bedrooms: { value: input.bedrooms?.value ?? 2, hard: input.bedrooms?.locked ?? false },
    bathrooms: { value: input.bathrooms?.value ?? 1, hard: input.bathrooms?.locked ?? false },
    symmetricFront: {
      value: input.symmetricFront?.value ?? false,
      hard: input.symmetricFront?.locked ?? false,
    },
    basement: {
      value: input.basement?.value ?? false,
      hard: input.basement?.locked ?? false,
    },
    garage: {
      value: input.garage?.value ?? false,
      hard: input.garage?.locked ?? false,
    },
  };
}
